import { createContext, useContext, useState, useEffect } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { API_BASE_URL } from "../../config";

// Create AuthContext
const AuthContext = createContext();

// Create AuthProvider component to wrap the app and provide AuthContext
export const AuthProvider = ({ children }) => {
  const [jwt, setJwt] = useState(Cookies.get("jwt") || null);
  const [data, setData] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [roleName, setRoleName] = useState(null);
  const [dayEndPending, setDayEndPending] = useState(false);
  const [isCheckingDayEnd, setIsCheckingDayEnd] = useState(true);

  // Function to check yesterday's day-end status - OPTIMIZED
  // Uses the dedicated backend API instead of fetching all transactions
  const checkDayEndStatus = async () => {
    if (!data?.branchId) {
      setIsCheckingDayEnd(false);
      return;
    }

    try {
      setIsCheckingDayEnd(true);

      // Use the optimized backend API that does the check with efficient SQL
      const response = await axios.get(`${API_BASE_URL}/api/checkYesterdayDayEnd`, {
        params: { branchId: data.branchId }
      });

      setDayEndPending(response.data?.data?.isPending || false);
    } catch (err) {
      console.error('Error checking day-end status:', err);
      setDayEndPending(false);
    } finally {
      setIsCheckingDayEnd(false);
    }
  };

  // Check day-end status when user data changes
  useEffect(() => {
    if (data?.branchId) {
      checkDayEndStatus();
    }
  }, [data?.branchId]);

  const login = (token) => {
    // console.log("token info: " + JSON.stringify(token));
    setJwt(token);
    Cookies.set("jwt", token, { expires: 24 });
  };

  const logout = () => {
    setJwt(null);
    Cookies.remove("jwt");
    setData(null);
    setDayEndPending(false);

    // Clear all dashboard cache keys to prevent stale data on next login
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('dashboardData')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };

  const isAuthenticated = () => {
    return !!jwt;
  };

  const updateData = (newData) => {
    setData(newData);
  };

  const updatePermissions = (newPermissions) => {
    setPermissions(newPermissions);
  };

  const updateRoleName = (newRoleName) => {
    setRoleName(newRoleName);
  };

  return (
    <AuthContext.Provider
      value={{
        jwt,
        login,
        logout,
        isAuthenticated,
        data,
        updateData,
        permissions,
        updatePermissions,
        roleName,
        updateRoleName,
        dayEndPending,
        isCheckingDayEnd,
        checkDayEndStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Create useAuth hook to access AuthContext
export const useAuth = () => useContext(AuthContext);
